import React from 'react';
import styled from 'styled-components';
import {
  gray30,
  gray20,
  white,
  screenXxsMax,
  cssColorValue,
  cssSpacing,
  Text,
  LogoSpotifyForArtists,
} from '@spotify-internal/encore-web';
import { PageHead, useLayoutContext } from '../../features/Layout';
import artistImg from './artist.png';
import labelImg from './label.png';
import { useT } from '@spotify-internal/i18n-core';
import { InteractiveElement } from '../../utils/InteractiveElement';

const TRACK_CAT = 'Claim';

const Header = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: ${cssSpacing('looser')};
  .login {
    color: inherit;
    font-size: inherit;
    font-weight: bold;
  }
`;

const HideWhenXxs = styled.span`
  @media (max-width: ${screenXxsMax}) {
    display: none;
  }
`;

const MainCenterer = styled.div`
  align-items: center;
  display: flex;
  flex-grow: 1;
  justify-content: center;
  padding: ${cssSpacing('tighter')};
`;

const Actions = styled.div`
  .button {
    border: solid 2px ${gray30};
    color: ${cssColorValue('textBase')};
    border-radius: 10px;
    display: inline-block;
    font-size: 18px;
    font-weight: bold;
    margin: ${cssSpacing('looser-2')};
    padding: ${cssSpacing('looser-2')} ${cssSpacing('looser')};
    transition: box-shadow 0.2s;
    width: 328px;

    &:hover {
      background: ${gray20};
      box-shadow: 0 7px 20px rgba(0, 0, 0, 0.8);
    }

    @media (max-width: ${screenXxsMax}) {
      display: block;
      width: auto;
    }
  }
`;

const Main = styled.div`
  text-align: center;
  width: 800px;
`;

const Image = styled.img`
  background-color: ${cssColorValue('decorativeSubdued')};
  border-radius: 100%;
  height: 132px;
  margin-left: auto;
  margin-right: auto;
  margin-top: ${cssSpacing('base')};
  width: 132px;
`;

const Chrome = styled.div`
  color: ${cssColorValue('textBase')};
  background-color: ${cssColorValue('backgroundBase')};
  background-position: center;
  background-repeat: no-repeat;
  background-size: 100% 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Title = styled(Text).attrs({
  variant: 'titleLarge',
})`
  font-size: 56px;
  line-height: 64px;
  display: block;
  margin-bottom: ${cssSpacing('base')};
`;

const Subtitle = styled(Text).attrs({})`
  font-size: 18px;
  display: block;
  margin-bottom: ${cssSpacing('looser')};
`;

const HelpText = styled(Text).attrs({
  variant: 'bodyMedium',
})`
  display: block;
  margin-top: ${cssSpacing('tighter-2')};

  .link {
    color: ${white} !important;
    text-decoration: underline;
    &:hover {
      opacity: 0.7;
    }
    &[href]:hover:not([disabled]) {
      color: ${white};
    }
  }
`;

const ClaimPage = () => {
  const t = useT();
  const { setShowFooter, setShowNavBar } = useLayoutContext();

  React.useEffect(() => {
    setShowFooter(false);
    setShowNavBar(false);
    return () => {
      setShowFooter(true);
      setShowNavBar(true);
    };
  }, []);

  return (
    <Chrome className="encore-inverted-dark-set">
      <PageHead
        title={t('FRODOR_7b3ef2', 'Get access', '')}
        description={t(
          'FRODOR_3c797c',
          'Get access to Spotify for Artists',
          '',
        )}
      />
      <Header>
        <InteractiveElement
          href="/"
          trackingLabel="Logo"
          trackingCategory={TRACK_CAT}
        >
          <LogoSpotifyForArtists height={36} condensed />
        </InteractiveElement>
        <Text variant="bodyMedium">
          <HideWhenXxs>
            {t('FRODOR_aece88', 'Already part of a team?', '')}
          </HideWhenXxs>{' '}
          <InteractiveElement
            className="login"
            trackingCategory={TRACK_CAT}
            trackingLabel="Log in"
            href="https://artists.spotify.com/c/"
          >
            {t('FRODOR_284ac7', 'Log in', '')}
          </InteractiveElement>
        </Text>
      </Header>
      <MainCenterer>
        <Main>
          <Title>
            {t('FRODOR_3c797c', 'Get access to Spotify for Artists', '')}
          </Title>
          <Subtitle>
            {t('FRODOR_09b31f', 'First, tell us who you are.', '')}
          </Subtitle>
          <Actions>
            <InteractiveElement
              className="button"
              href="https://artists.spotify.com/c/team/access/artist"
              trackingLabel="Artist"
              trackingCategory={TRACK_CAT}
            >
              <div>{t('FRODOR_06a5f9', 'Artist or manager', '')}</div>
              <Image
                alt={t('FRODOR_0d25d0', 'Artist', '')}
                // @ts-ignore
                src={artistImg}
              />
            </InteractiveElement>
            <InteractiveElement
              className="button"
              href="https://artists.spotify.com/c/team/access/label"
              trackingLabel="Label"
              trackingCategory={TRACK_CAT}
            >
              <div>{t('FRODOR_afa16e', 'Label team member', '')}</div>
              <Image
                alt={t('FRODOR_6b9c97', 'Label', '')}
                // @ts-ignore
                src={labelImg}
              />
            </InteractiveElement>
          </Actions>
          <HelpText>
            <InteractiveElement
              className="link"
              trackingLabel="FAQ Link"
              trackingCategory={TRACK_CAT}
              href="/help/article/inviting-team-members"
            >
              {t(
                'FRODOR_c4fa1f',
                'If your team already exists, ask an admin for access.',
                'This text is for a link to an article on how to ask for artist access',
              )}
            </InteractiveElement>
          </HelpText>
          <HelpText>
            <InteractiveElement
              className="link"
              trackingLabel="FAQ Link"
              trackingCategory={TRACK_CAT}
              href="/help/article/getting-access-to-spotify-for-artists"
            >
              {t(
                'FRODOR_f71751',
                'Not sure which to choose?',
                'Not sure is a shortened version of Are you not sure',
              )}
            </InteractiveElement>
          </HelpText>
        </Main>
      </MainCenterer>
    </Chrome>
  );
};

export default ClaimPage;
